import React, { useEffect, useState } from 'react';
import { Edit3, Plus, Tags, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';
const emptyForm = { name: '', description: '', unitsText: '' };

function parseUnits(value) {
  const seen = new Set();
  return value
    .split(',')
    .map(unit => unit.trim())
    .filter(unit => {
      const key = unit.toLowerCase();
      if (!unit || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => apiGet('/merchant/categories').then(r => setCategories(r.data || [])).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const reset = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setSaving(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setForm({
      name: category.name || '',
      description: category.description || '',
      unitsText: (category.units || []).join(', '),
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const units = parseUnits(form.unitsText);
    if (units.length === 0) {
      toast.error('Add at least one unit');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        units,
      };
      if (editing) {
        await apiPatch(`/merchant/categories/${editing.id}`, payload);
        toast.success('Category updated');
      } else {
        await apiPost('/merchant/categories', payload);
        toast.success('Category created');
      }
      reset();
      await load();
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  };

  const remove = async (category) => {
    if (!window.confirm(`Deactivate ${category.name}? Existing products will keep their category text.`)) return;
    setDeletingId(category.id);
    try {
      await apiDelete(`/merchant/categories/${category.id}`);
      toast.success('Category deactivated');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="categories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Categories</h1>
        <button onClick={openCreate} className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2" data-testid="add-category-btn">
          <Plus className="w-4 h-4" /> Add category
        </button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {categories.length === 0 && <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-rozops-text-muted col-span-full">No categories yet. Add categories with units, like Milk with ml and litres.</div>}
        {categories.map(category => (
          <div key={category.id} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5" data-testid={`category-${category.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="w-10 h-10 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center"><Tags className="w-5 h-5" /></div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => openEdit(category)} title="Edit category" aria-label={`Edit ${category.name}`} className="w-9 h-9 rounded-lg text-rozops-text-muted hover:bg-gray-50 hover:text-rozops-blue grid place-items-center">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(category)} disabled={deletingId === category.id} title="Deactivate category" aria-label={`Deactivate ${category.name}`} className="w-9 h-9 rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-50 grid place-items-center">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 font-heading font-bold text-lg text-rozops-navy break-words">{category.name}</div>
            {category.description && <div className="text-sm text-rozops-text-muted break-words">{category.description}</div>}
            <div className="mt-3 flex flex-wrap gap-2">
              {(category.units || []).map(unit => (
                <span key={unit} className="rounded-md bg-gray-50 px-2 py-1 text-xs font-semibold text-rozops-text-muted">{unit}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={reset}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-heading font-bold text-xl text-rozops-navy">{editing ? 'Edit category' : 'Add category'}</h2>
              <button type="button" onClick={reset} className="w-8 h-8 rounded-lg hover:bg-gray-50 text-rozops-text-muted grid place-items-center" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input required placeholder="Category name (e.g. Milk)" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name" />
              <input required placeholder="Units, comma separated (e.g. ml, litre)" className={inputCls} value={form.unitsText} onChange={(e) => setForm({ ...form, unitsText: e.target.value })} data-testid="category-units" />
              <input placeholder="Description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="rounded-xl bg-rozops-bg px-4 py-3 text-xs text-rozops-text-muted">
                Examples: Milk: ml, litre. Sugar: gm, kg.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={reset} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" disabled={saving} className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold disabled:opacity-60" data-testid="category-submit">{saving ? 'Saving...' : editing ? 'Save changes' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
